/**
 * CropService - Unified Crop Management
 *
 * This service provides a single source of truth for all crop-related operations.
 * It uses a NormalizedCrop model that represents crops in a consistent format,
 * regardless of the source (soft crop, order XML, pixel coordinates, etc.)
 *
 * KEY CONCEPTS:
 *
 * 1. NormalizedCrop: All crops are stored as percentages relative to the ORIGINAL
 *    (unrotated) image dimensions. This ensures consistent behavior regardless of
 *    how the image is displayed.
 *
 * 2. Rotation is tracked separately: The crop coordinates are always in the original
 *    image space. The rotation value indicates how the image should be displayed,
 *    and conversions handle the coordinate space transformation when needed.
 *
 * 3. Aspect Ratio Flipping: When a landscape image goes into a portrait slot (or vice versa),
 *    we may need to "flip" the target aspect ratio. This service provides clear logic
 *    for determining when flipping is appropriate.
 */

import type { Coordinates } from 'vue-advanced-cropper';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * Valid rotation values (normalized to 0, 90, 180, or 270 degrees)
 */
export type NormalizedRotation = 0 | 90 | 180 | 270;

/**
 * Simple width/height dimensions
 */
export interface Dimensions {
  width: number;
  height: number;
}

/**
 * The unified crop representation used throughout the application.
 * All values are percentages (0-100) relative to the ORIGINAL image dimensions.
 */
export interface NormalizedCrop {
  /** X position of crop center as percentage of image width (0-100) */
  centerX: number;
  /** Y position of crop center as percentage of image height (0-100) */
  centerY: number;
  /** Width of crop as percentage of image width (0-100) */
  widthPct: number;
  /** Height of crop as percentage of image height (0-100) */
  heightPct: number;
  /** Image rotation in degrees */
  rotation: NormalizedRotation;
}

/**
 * Pixel-based crop coordinates (used by vue-advanced-cropper)
 */
export interface PixelCrop {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Soft crop format used on SubjectImage models
 */
export interface SoftCrop {
  cropX: number; // Width percentage (0-100)
  cropY: number; // Height percentage (0-100)
  xMid: number; // Center X percentage (0-100)
  yMid: number; // Center Y percentage (0-100)
  rotation?: number;
}

/**
 * Package node position coordinates format: "left top bottom right width height"
 */
export interface NodeDimensions {
  left: number;
  top: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

// =============================================================================
// NORMALIZATION HELPERS
// =============================================================================

/**
 * Normalizes any rotation value to 0, 90, 180, or 270
 */
export function normalizeRotation(rotation: number): NormalizedRotation {
  const normalized = ((rotation % 360) + 360) % 360;
  // Round to nearest 90-degree increment
  const rounded = Math.round(normalized / 90) * 90;
  return (rounded % 360) as NormalizedRotation;
}

/**
 * Checks if rotation causes width/height to swap (90 or 270 degrees)
 */
export function isRotationSwapped(rotation: NormalizedRotation): boolean {
  return rotation === 90 || rotation === 270;
}

/**
 * Creates a NormalizedCrop from display-space coordinates
 * Use this when you have coordinates from a rotated/displayed image view
 * and need to convert them to original image space for storage
 */
export function fromDisplaySpace(
  displayCenterX: number,
  displayCenterY: number,
  displayWidthPct: number,
  displayHeightPct: number,
  rotation: NormalizedRotation,
): NormalizedCrop {
  // Transform from display space to original space
  switch (rotation) {
    case 0:
      return {
        centerX: displayCenterX,
        centerY: displayCenterY,
        widthPct: displayWidthPct,
        heightPct: displayHeightPct,
        rotation,
      };

    case 90:
      // Display (x, y) came from original (y, 100-x) with swapped w/h
      return {
        centerX: displayCenterY,
        centerY: 100 - displayCenterX,
        widthPct: displayHeightPct,
        heightPct: displayWidthPct,
        rotation,
      };

    case 180:
      return {
        centerX: 100 - displayCenterX,
        centerY: 100 - displayCenterY,
        widthPct: displayWidthPct,
        heightPct: displayHeightPct,
        rotation,
      };

    case 270:
      // Display (x, y) came from original (100-y, x) with swapped w/h
      return {
        centerX: 100 - displayCenterY,
        centerY: displayCenterX,
        widthPct: displayHeightPct,
        heightPct: displayWidthPct,
        rotation,
      };

    default:
      return {
        centerX: displayCenterX,
        centerY: displayCenterY,
        widthPct: displayWidthPct,
        heightPct: displayHeightPct,
        rotation,
      };
  }
}

/**
 * Converts a NormalizedCrop (original space) to display-space coordinates
 * Use this when you need to position elements on a rotated/displayed image view
 */
export function toDisplaySpace(crop: NormalizedCrop): {
  centerX: number;
  centerY: number;
  widthPct: number;
  heightPct: number;
} {
  switch (crop.rotation) {
    case 0:
      return {
        centerX: crop.centerX,
        centerY: crop.centerY,
        widthPct: crop.widthPct,
        heightPct: crop.heightPct,
      };

    case 90:
      // Original (x, y) -> Display (100-y, x) with swapped w/h
      return {
        centerX: 100 - crop.centerY,
        centerY: crop.centerX,
        widthPct: crop.heightPct,
        heightPct: crop.widthPct,
      };

    case 180:
      return {
        centerX: 100 - crop.centerX,
        centerY: 100 - crop.centerY,
        widthPct: crop.widthPct,
        heightPct: crop.heightPct,
      };

    case 270:
      // Original (x, y) -> Display (y, 100-x) with swapped w/h
      return {
        centerX: crop.centerY,
        centerY: 100 - crop.centerX,
        widthPct: crop.heightPct,
        heightPct: crop.widthPct,
      };

    default:
      return {
        centerX: crop.centerX,
        centerY: crop.centerY,
        widthPct: crop.widthPct,
        heightPct: crop.heightPct,
      };
  }
}

/**
 * Gets the effective dimensions after rotation is applied
 */
export function getRotatedDimensions(dims: Dimensions, rotation: NormalizedRotation): Dimensions {
  if (isRotationSwapped(rotation)) {
    return { width: dims.height, height: dims.width };
  }
  return { ...dims };
}

// =============================================================================
// CONVERSION: FROM OTHER FORMATS TO NormalizedCrop
// =============================================================================

/**
 * Creates a NormalizedCrop from soft crop values (SubjectImage format)
 */
export function fromSoftCrop(softCrop: SoftCrop): NormalizedCrop {
  return {
    centerX: softCrop.xMid,
    centerY: softCrop.yMid,
    widthPct: softCrop.cropX,
    heightPct: softCrop.cropY,
    rotation: normalizeRotation(softCrop.rotation ?? 0),
  };
}

/**
 * Creates a NormalizedCrop from Order XML format string
 * Format: "xMid yMid cropX cropY" (4 parts)
 */
export function fromOrderXml(orderXml: string, rotation: number = 0): NormalizedCrop {
  const parts = orderXml.split(' ').map(Number);

  if (parts.length === 4) {
    const [xMid, yMid, cropX, cropY] = parts;
    return {
      centerX: xMid,
      centerY: yMid,
      widthPct: cropX,
      heightPct: cropY,
      rotation: normalizeRotation(rotation),
    };
  }

  // Handle 6-part format: "xMid yMid ? ? cropX cropY"
  if (parts.length === 6) {
    const [xMid, yMid, , , cropX, cropY] = parts;
    return {
      centerX: xMid,
      centerY: yMid,
      widthPct: cropX,
      heightPct: cropY,
      rotation: normalizeRotation(rotation),
    };
  }

  // Default: no crop (full image, centered)
  return {
    centerX: 50,
    centerY: 50,
    widthPct: 100,
    heightPct: 100,
    rotation: normalizeRotation(rotation),
  };
}

/**
 * Creates a NormalizedCrop from pixel coordinates and image dimensions
 * The pixel coordinates are assumed to be in the DISPLAY space (after rotation)
 */
export function fromPixelCrop(
  pixelCrop: PixelCrop,
  imageDimensions: Dimensions,
  rotation: NormalizedRotation = 0,
): NormalizedCrop {
  // Get the effective dimensions based on rotation
  const effectiveDims = getRotatedDimensions(imageDimensions, rotation);

  // Calculate center and size as percentages of effective (displayed) dimensions
  const displayCenterX = pixelCrop.left + pixelCrop.width / 2;
  const displayCenterY = pixelCrop.top + pixelCrop.height / 2;
  const displayWidthPct = (pixelCrop.width / effectiveDims.width) * 100;
  const displayHeightPct = (pixelCrop.height / effectiveDims.height) * 100;

  // Transform back to original image space
  const { centerX, centerY, widthPct, heightPct } = transformDisplayToOriginal(
    {
      centerX: (displayCenterX / effectiveDims.width) * 100,
      centerY: (displayCenterY / effectiveDims.height) * 100,
      widthPct: displayWidthPct,
      heightPct: displayHeightPct,
    },
    rotation,
  );

  return {
    centerX,
    centerY,
    widthPct,
    heightPct,
    rotation,
  };
}

/**
 * Creates a NormalizedCrop from vue-advanced-cropper Coordinates
 */
export function fromCropperCoordinates(
  coords: Coordinates,
  imageDimensions: Dimensions,
  rotation: NormalizedRotation = 0,
): NormalizedCrop {
  return fromPixelCrop(
    {
      left: coords.left,
      top: coords.top,
      width: coords.width,
      height: coords.height,
    },
    imageDimensions,
    rotation,
  );
}

// =============================================================================
// CONVERSION: FROM NormalizedCrop TO OTHER FORMATS
// =============================================================================

/**
 * Converts NormalizedCrop to Order XML format string
 * Always outputs in 4-part format: "xMid yMid cropX cropY"
 *
 * Note: OrderXML stores coordinates in the same space as NormalizedCrop
 * (original image space, not display space). No transformation is needed.
 */
export function toOrderXml(crop: NormalizedCrop): string {
  // OrderXML stores coordinates directly - no transformation needed
  return `${Math.round(crop.centerX)} ${Math.round(crop.centerY)} ${Math.round(crop.widthPct)} ${Math.round(crop.heightPct)}`;
}

/**
 * Converts NormalizedCrop to pixel coordinates for a given image size
 * Returns coordinates in the DISPLAY space (after rotation)
 */
export function toPixelCrop(crop: NormalizedCrop, imageDimensions: Dimensions): PixelCrop {
  // Transform to display space
  const display = transformOriginalToDisplay(crop, crop.rotation);
  const effectiveDims = getRotatedDimensions(imageDimensions, crop.rotation);

  const width = (display.widthPct / 100) * effectiveDims.width;
  const height = (display.heightPct / 100) * effectiveDims.height;
  const centerX = (display.centerX / 100) * effectiveDims.width;
  const centerY = (display.centerY / 100) * effectiveDims.height;

  return {
    left: centerX - width / 2,
    top: centerY - height / 2,
    width,
    height,
  };
}

/**
 * Converts NormalizedCrop to SoftCrop format
 */
export function toSoftCrop(crop: NormalizedCrop): SoftCrop {
  return {
    xMid: crop.centerX,
    yMid: crop.centerY,
    cropX: crop.widthPct,
    cropY: crop.heightPct,
    rotation: crop.rotation,
  };
}

/**
 * Converts NormalizedCrop to the format used by vue-advanced-cropper
 */
export function toCropperCoordinates(crop: NormalizedCrop, imageDimensions: Dimensions): Coordinates {
  const pixel = toPixelCrop(crop, imageDimensions);
  return {
    left: pixel.left,
    top: pixel.top,
    width: pixel.width,
    height: pixel.height,
  };
}

// =============================================================================
// COORDINATE SPACE TRANSFORMATIONS
// =============================================================================

interface PercentageCrop {
  centerX: number;
  centerY: number;
  widthPct: number;
  heightPct: number;
}

/**
 * Transforms crop coordinates from ORIGINAL image space to DISPLAY space
 * (accounts for rotation)
 */
function transformOriginalToDisplay(crop: PercentageCrop, rotation: NormalizedRotation): PercentageCrop {
  switch (rotation) {
    case 0:
      return { ...crop };

    case 90:
      // Rotate 90° clockwise: (x, y) -> (100-y, x), swap width/height
      return {
        centerX: 100 - crop.centerY,
        centerY: crop.centerX,
        widthPct: crop.heightPct,
        heightPct: crop.widthPct,
      };

    case 180:
      // Rotate 180°: (x, y) -> (100-x, 100-y)
      return {
        centerX: 100 - crop.centerX,
        centerY: 100 - crop.centerY,
        widthPct: crop.widthPct,
        heightPct: crop.heightPct,
      };

    case 270:
      // Rotate 270° clockwise (90° counter-clockwise): (x, y) -> (y, 100-x), swap width/height
      return {
        centerX: crop.centerY,
        centerY: 100 - crop.centerX,
        widthPct: crop.heightPct,
        heightPct: crop.widthPct,
      };

    default:
      return { ...crop };
  }
}

/**
 * Transforms crop coordinates from DISPLAY space back to ORIGINAL image space
 * (inverse of transformOriginalToDisplay)
 */
function transformDisplayToOriginal(displayCrop: PercentageCrop, rotation: NormalizedRotation): PercentageCrop {
  // The inverse of each rotation
  switch (rotation) {
    case 0:
      return { ...displayCrop };

    case 90:
      // Inverse of 90° clockwise is 270° clockwise
      return {
        centerX: displayCrop.centerY,
        centerY: 100 - displayCrop.centerX,
        widthPct: displayCrop.heightPct,
        heightPct: displayCrop.widthPct,
      };

    case 180:
      // Inverse of 180° is 180°
      return {
        centerX: 100 - displayCrop.centerX,
        centerY: 100 - displayCrop.centerY,
        widthPct: displayCrop.widthPct,
        heightPct: displayCrop.heightPct,
      };

    case 270:
      // Inverse of 270° clockwise is 90° clockwise
      return {
        centerX: 100 - displayCrop.centerY,
        centerY: displayCrop.centerX,
        widthPct: displayCrop.heightPct,
        heightPct: displayCrop.widthPct,
      };

    default:
      return { ...displayCrop };
  }
}

// =============================================================================
// ASPECT RATIO & FLIP LOGIC
// =============================================================================

/**
 * Calculates the aspect ratio of a crop
 * @param crop The normalized crop
 * @param imageDimensions The original image dimensions (needed to get actual ratio)
 * @returns The aspect ratio (width / height) of the cropped region
 */
export function getCropAspectRatio(crop: NormalizedCrop, imageDimensions: Dimensions): number {
  const cropWidth = (crop.widthPct / 100) * imageDimensions.width;
  const cropHeight = (crop.heightPct / 100) * imageDimensions.height;

  // If rotated 90 or 270, the displayed aspect ratio is inverted
  if (isRotationSwapped(crop.rotation)) {
    return cropHeight / cropWidth;
  }

  return cropWidth / cropHeight;
}

/**
 * Determines if the aspect ratio should be flipped when fitting a crop into a target slot.
 *
 * Use this when initializing a crop: if the image's natural crop orientation is opposite
 * to the target slot orientation, flipping the aspect ratio will produce better results.
 *
 * @param cropAspectRatio The aspect ratio of the source crop (width / height)
 * @param targetAspectRatio The aspect ratio of the target slot (width / height)
 * @returns true if the aspect ratio should be inverted
 */
export function shouldFlipAspectRatio(cropAspectRatio: number, targetAspectRatio: number): boolean {
  // Calculate how different the crop is from the target in both orientations
  const unflippedDiff = Math.abs(targetAspectRatio - cropAspectRatio);
  const flippedDiff = Math.abs(targetAspectRatio - 1 / cropAspectRatio);

  // If the inverted crop ratio is closer to the target, we should flip
  return unflippedDiff > flippedDiff;
}

/**
 * Gets the effective aspect ratio for a package node, optionally inverted
 * @param nodeWidth The node width from positionCoords
 * @param nodeHeight The node height from positionCoords
 * @param flip Whether to invert the aspect ratio
 */
export function getNodeAspectRatio(nodeWidth: number, nodeHeight: number, flip: boolean): number {
  return flip ? nodeHeight / nodeWidth : nodeWidth / nodeHeight;
}

/**
 * Standard portrait crop aspect ratio (8x10 => 0.8).
 */
export const PORTRAIT_CROP_ASPECT_RATIO = 8 / 10;

/**
 * Standard landscape crop aspect ratio (7x5 => 1.4).
 */
export const LANDSCAPE_CROP_ASPECT_RATIO = 7 / 5;

/**
 * Determines the crop aspect ratio to use based purely on the SOURCE IMAGE orientation,
 * independent of any package node / slot shape.
 *
 * - Vertical image (height > width)   => 8x10 portrait (0.8)
 * - Horizontal image (width > height) => 7x5 landscape (1.4)
 * - Square image (width === height)   => treated as portrait (8x10)
 *
 * Rationale: center-cropping at these ratios keeps head-spacing constant top/bottom while
 * the sides grow/shrink, which matches how portraits should frame. This intentionally does
 * NOT derive aspect from an item's node ratio.
 *
 * Orientation is judged on the DISPLAYED image, so a rotated portrait (90/270) that displays
 * as landscape is classified by its displayed dimensions.
 *
 * @param imageDimensions The original (unrotated) image dimensions
 * @param rotation The image rotation (defaults to 0)
 * @returns The target crop aspect ratio (width / height)
 */
export function getOrientationCropAspectRatio(
  imageDimensions: Dimensions,
  rotation: NormalizedRotation = 0,
): number {
  const displayed = getRotatedDimensions(imageDimensions, rotation);
  // Square ties resolve to portrait (8x10) per product decision (isLandscape is strict >).
  return isLandscape(displayed) ? LANDSCAPE_CROP_ASPECT_RATIO : PORTRAIT_CROP_ASPECT_RATIO;
}

/**
 * Parses the positionCoords string from a PackageItemImageNode
 * Supports multiple formats:
 * - DP2 format (tilde-separated, 6 parts): "left~top~bottom~right~width~height"
 * - Space-separated (6 parts): "left top bottom right width height"
 * - Space-separated (4 parts): "left top width height"
 */
export function parsePositionCoords(positionCoords: string): NodeDimensions {
  // Check for DP2 format (tilde-separated)
  if (positionCoords.includes('~')) {
    const parts = positionCoords.split('~').map(Number);
    if (parts.length === 6) {
      return {
        left: parts[0],
        top: parts[1],
        bottom: parts[2],
        right: parts[3],
        width: parts[4],
        height: parts[5],
      };
    }
    // Fall through to error handling if tilde format is malformed
  }

  // Space-separated formats
  const parts = positionCoords.split(' ').map(Number);

  if (parts.length === 6) {
    return {
      left: parts[0],
      top: parts[1],
      bottom: parts[2],
      right: parts[3],
      width: parts[4],
      height: parts[5],
    };
  }

  if (parts.length === 4) {
    // 4-part format: "left top width height"
    return {
      left: parts[0],
      top: parts[1],
      bottom: 0,
      right: 0,
      width: parts[2],
      height: parts[3],
    };
  }

  // Default to 8x10 aspect ratio (portrait) rather than square
  console.warn('Invalid positionCoords format, expected 4 or 6 parts:', positionCoords);
  return { left: 0, top: 0, bottom: 0, right: 0, width: 8, height: 10 };
}

// =============================================================================
// CROP CALCULATION
// =============================================================================

/**
 * Creates a default crop (centered, full image)
 */
export function createDefaultCrop(rotation: NormalizedRotation = 0): NormalizedCrop {
  return {
    centerX: 50,
    centerY: 50,
    widthPct: 100,
    heightPct: 100,
    rotation,
  };
}

/**
 * Calculates the initial crop for fitting an image into a target aspect ratio.
 * This performs a "cover" crop - the crop will cover the entire target area
 * while maintaining the target aspect ratio and being as large as possible.
 *
 * @param imageDimensions The original image dimensions
 * @param targetAspectRatio The target aspect ratio (width / height)
 * @param softCrop Optional existing soft crop to use as the base region
 * @param rotation The image rotation
 * @returns A NormalizedCrop that fits the target aspect ratio
 */
export function calculateInitialCrop(
  imageDimensions: Dimensions,
  targetAspectRatio: number,
  softCrop?: SoftCrop,
  rotation: NormalizedRotation = 0,
): NormalizedCrop {
  // Start with the soft crop or full image
  const baseCrop = softCrop ? fromSoftCrop(softCrop) : createDefaultCrop(rotation);
  baseCrop.rotation = rotation;

  // Get the effective dimensions after rotation
  const effectiveDims = getRotatedDimensions(imageDimensions, rotation);

  // Calculate the crop region in pixels
  const cropWidthPx = (baseCrop.widthPct / 100) * imageDimensions.width;
  const cropHeightPx = (baseCrop.heightPct / 100) * imageDimensions.height;

  // If rotation swaps dimensions, adjust the crop aspect ratio calculation
  let effectiveCropWidth = cropWidthPx;
  let effectiveCropHeight = cropHeightPx;
  if (isRotationSwapped(rotation)) {
    effectiveCropWidth = cropHeightPx;
    effectiveCropHeight = cropWidthPx;
  }

  const cropAspectRatio = effectiveCropWidth / effectiveCropHeight;

  // Determine the constrained dimensions to fit the target aspect ratio
  let newWidthPct: number;
  let newHeightPct: number;

  if (cropAspectRatio > targetAspectRatio) {
    // Crop is wider than target - constrain width
    newHeightPct = baseCrop.heightPct;
    if (isRotationSwapped(rotation)) {
      // When rotated, height% affects displayed width
      const constrainedWidth = effectiveCropHeight * targetAspectRatio;
      newWidthPct = (constrainedWidth / imageDimensions.height) * 100;
    } else {
      const constrainedWidth = (baseCrop.heightPct / 100) * imageDimensions.height * targetAspectRatio;
      newWidthPct = (constrainedWidth / imageDimensions.width) * 100;
    }
  } else {
    // Crop is taller than target - constrain height
    newWidthPct = baseCrop.widthPct;
    if (isRotationSwapped(rotation)) {
      // When rotated, width% affects displayed height
      const constrainedHeight = effectiveCropWidth / targetAspectRatio;
      newHeightPct = (constrainedHeight / imageDimensions.width) * 100;
    } else {
      const constrainedHeight = ((baseCrop.widthPct / 100) * imageDimensions.width) / targetAspectRatio;
      newHeightPct = (constrainedHeight / imageDimensions.height) * 100;
    }
  }

  return {
    centerX: baseCrop.centerX,
    centerY: baseCrop.centerY,
    widthPct: Math.min(100, Math.max(0, newWidthPct)),
    heightPct: Math.min(100, Math.max(0, newHeightPct)),
    rotation,
  };
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Checks if two crops are effectively equal (within a small tolerance)
 */
export function cropsEqual(a: NormalizedCrop, b: NormalizedCrop, tolerance: number = 0.5): boolean {
  return (
    Math.abs(a.centerX - b.centerX) <= tolerance &&
    Math.abs(a.centerY - b.centerY) <= tolerance &&
    Math.abs(a.widthPct - b.widthPct) <= tolerance &&
    Math.abs(a.heightPct - b.heightPct) <= tolerance &&
    a.rotation === b.rotation
  );
}

/**
 * Validates that a crop is within bounds
 */
export function validateCrop(crop: NormalizedCrop): NormalizedCrop {
  return {
    centerX: Math.max(0, Math.min(100, crop.centerX)),
    centerY: Math.max(0, Math.min(100, crop.centerY)),
    widthPct: Math.max(1, Math.min(100, crop.widthPct)),
    heightPct: Math.max(1, Math.min(100, crop.heightPct)),
    rotation: crop.rotation,
  };
}

/**
 * Checks if an image is landscape orientation
 */
export function isLandscape(dimensions: Dimensions): boolean {
  return dimensions.width > dimensions.height;
}

/**
 * Checks if an image is portrait orientation
 */
export function isPortrait(dimensions: Dimensions): boolean {
  return dimensions.height > dimensions.width;
}
